import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import moment from 'moment';
import { PlatformIChingConsultationDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-dto';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { NOTIFIER } from '@shared/notifier/types';
import { SharedModule } from '@shared/shared.module';
import { HdChartSettingsStore } from '../../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { analyse, linesFromHexagram } from '../core/iching-core';
import {
  IChingCastDistribution,
  IChingCastMode,
  IChingCoinFace,
  IChingLinePosition,
  IChingLineValue,
  IChingMethodContent,
  IChingMethodStep,
  IChingReading,
} from '../core/iching.models';
import { HdIChingContentService } from '../hd-iching-content.service';
import { HdIChingJournalListComponent } from '../journal/hd-iching-journal-list.component';
import { HdIChingJournalService } from '../journal/hd-iching-journal.service';
import { IChingConsultContext } from '../moment/hd-iching-moment.models';
import { HdIChingTransitBlockComponent } from '../moment/hd-iching-transit-block.component';
import { injectIChingHexagramOpener } from '../moment/hd-iching-modal-panel';
import { HdIChingConsultationSession, withoutSaved } from './hd-iching-consultation-session';
import { HdIChingGuidePanelService } from './hd-iching-guide-panel.service';
import { EMPTY_ICHING_DRAFT, IChingDraft, clearIChingDraft, loadIChingDraft, saveIChingDraft } from './hd-iching-draft';
import { HdIChingReadingContentStore, IChingReadingContentKey, sameReadingContentKey } from './hd-iching-reading-content.store';
import { injectIChingTranslate } from './iching-consult-translate';
import { HdIChingReadingComponent } from './reading/hd-iching-reading.component';
import { buildConsultationText } from './reading/iching-consultation-text';
import { HdIChingCastStepComponent } from './steps/hd-iching-cast-step.component';
import { HdIChingIntroComponent } from './steps/hd-iching-intro.component';
import { HdIChingQuestionStepComponent } from './steps/hd-iching-question-step.component';
import { HdIChingStepHelpComponent } from './steps/hd-iching-step-help.component';

/** Reflectievelden van het concept die fase 3 wijzigt. */
type IChingReflectionPatch = Partial<
  Pick<IChingDraft, 'relationMode' | 'nameRevealed' | 'keywords' | 'notes' | 'doList' | 'dontList'>
>;

/** Stand van de pagina: de lopende raadpleging of het dagboek. */
type IChingConsultView = 'consult' | 'journal';

/** Startwaarde uit de queryparameters (`?hexagram=N&moving=2,5`). */
export interface IChingConsultInitial {
  readonly hexagram: number;
  readonly moving: IChingLinePosition[];
}

/**
 * Raadpleegcomponent van de Classic I Ching-pagina: introductie, fase 1 (de
 * vraag), fase 2 (de worp) en, zodra zes lijnen er zijn, fase 3 (de duiding,
 * met bewaren in het dagboek en kopiëren), plus de begrippenlijst. Een toggle
 * in de kop wisselt naar het dagboek; een raadpleging daaruit heropent in fase 3.
 *
 * Verdeling: deze component houdt het concept (localStorage, `hd-iching-draft`),
 * de stand, de fases en de wiring; `HdIChingReadingContentStore` laadt de
 * content van fase 3 en `HdIChingConsultationSession` doet bewaren, herlezen,
 * heropenen en vergeten tegenover het dagboek. Worpmodus, kansverdeling en
 * ingeklapte intro staan in de settings (account-sync).
 *
 * Met `initial` (queryparameters) start een schoon concept in de directe modus
 * met dat hexagram, eenmalig per waarde; `initialApplied` laat de pagina de
 * parameters daarna wissen.
 *
 * **Ingebed** (`embedded`, de I Tjing-momentdialoog): geen kop, introductie of
 * begrippenlijst, en het concept leeft in het geheugen in plaats van in
 * localStorage, zodat het lopende concept van de pagina onaangeroerd blijft.
 * `context` levert dan de chart en het tijdstip van een nieuwe raadpleging, of
 * een bestaande raadpleging die meteen in de leesstand opent; `saved` meldt
 * elke geslaagde bewaarbeurt aan de host.
 */
@Component({
  selector: 'hd-iching-consult',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModule,
    ESplashLoaderComponent,
    ESegmentedToggleComponent,
    HdIChingJournalListComponent,
    HdIChingIntroComponent,
    HdIChingStepHelpComponent,
    HdIChingQuestionStepComponent,
    HdIChingCastStepComponent,
    HdIChingReadingComponent,
    HdIChingTransitBlockComponent,
  ],
  providers: [HdIChingReadingContentStore],
  templateUrl: './hd-iching-consult.component.html',
  styleUrl: './hd-iching-consult.component.scss',
})
export class HdIChingConsultComponent implements OnInit {
  readonly initial = input<IChingConsultInitial | undefined>(undefined);
  /** `initial` staat in het concept: de host mag de queryparameters wissen. */
  readonly initialApplied = output<void>();
  /** Ingebed in een dialoog: zonder kop, intro en begrippenlijst; concept in het geheugen. */
  readonly embedded = input(false, { transform: booleanAttribute });
  /** Chart en tijdstip van een nieuwe raadpleging, of een bestaande raadpleging (leesstand). */
  readonly context = input<IChingConsultContext | undefined>(undefined);
  /** Na elke geslaagde bewaarbeurt: de raadpleging zoals het dagboek ze teruggaf. */
  readonly saved = output<PlatformIChingConsultationDto>();

  private readonly content = inject(HdIChingContentService);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly guidePanel = inject(HdIChingGuidePanelService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly notifier = inject(NOTIFIER);
  /** In de momentdialoog zwevend (anders landt het leespaneel in de dock achter de dialoog). */
  private readonly openHexagramPanel = injectIChingHexagramOpener();
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  protected readonly readingContent = inject(HdIChingReadingContentStore);

  protected readonly t = injectIChingTranslate();

  protected readonly view = signal<IChingConsultView>('consult');
  protected readonly viewOptions = computed<ESegmentedToggleOption<IChingConsultView>[]>(() => [
    { value: 'consult', label: this.t('humanDesign.iching.consult.tabConsult') },
    { value: 'journal', label: this.t('humanDesign.iching.consult.tabJournal') },
  ]);

  /** Op de pagina geladen uit localStorage (ngOnInit); ingebed start het leeg of uit de context. */
  protected readonly draft = signal<IChingDraft>(EMPTY_ICHING_DRAFT);
  protected readonly method = signal<IChingMethodContent | undefined>(undefined);
  protected readonly methodError = signal(false);
  /** Het tijdstip van een bewaarde raadpleging is pas weer bewerkbaar na "Tijdstip aanpassen". */
  protected readonly timeEditOverride = signal(false);

  protected readonly castMode = computed(() => this.settings.ichingCastMode());
  protected readonly distribution = computed(() => this.settings.ichingCastDistribution());
  protected readonly introCollapsed = computed(() => this.settings.ichingIntroCollapsed());
  /** Alleen een nieuwe worp geeft een nieuwe duiding: notities typen laat ze ongemoeid. */
  protected readonly reading = computed<IChingReading | undefined>(
    () => {
      const lines = this.draft().lines;
      return lines.length === 6 ? analyse(lines) : undefined;
    },
    { equal: (a, b) => a?.lines.join() === b?.lines.join() },
  );

  protected readonly session = new HdIChingConsultationSession({
    journal: inject(HdIChingJournalService),
    notifier: this.notifier,
    t: this.t,
    draft: this.draft,
    castMode: this.castMode,
  });

  /**
   * Er staat iets dat bij sluiten of heropenen verloren gaat: een onbewaarde
   * raadpleging met invoer, of een bewaarde die sindsdien gewijzigd is.
   */
  readonly unsaved = computed(() =>
    this.session.savedId() === undefined ? hasUnsavedContent(this.draft()) : !this.session.saved(),
  );

  /** Chartnaam uit de context (dialoog); op de pagina is er geen naam bekend. */
  protected readonly contextChartName = computed(() => this.context()?.chartName ?? '');
  /** Tijdstip van de raadpleging in lokale tijd, voor het `datetime-local`-veld. */
  protected readonly consultedAtLocal = computed(() => {
    const at = this.draft().consultedAt;
    return at === undefined ? '' : moment.utc(at).local().format('YYYY-MM-DDTHH:mm');
  });
  protected readonly timeEditable = computed(() => this.session.savedId() === undefined || this.timeEditOverride());

  /** Laden pas met methode-content (zonder methode geen duiding), per worp en bron. */
  private readonly readingContentKey = computed<IChingReadingContentKey | undefined>(
    () => {
      const reading = this.reading();
      if (reading === undefined || this.method() === undefined) {
        return undefined;
      }
      return {
        source: this.content.sourceFor(this.contentLang.effectiveLang()),
        primary: reading.primary,
        changed: reading.changed,
        nuclear: reading.nuclearHexagram,
      };
    },
    { equal: sameReadingContentKey },
  );

  /** Laad-generatie: een nieuwere load (taalwissel) maakt een oudere stale. */
  private methodGeneration = 0;
  /** Laatst toegepaste `initial`, zodat dezelfde waarde het concept niet opnieuw overschrijft. */
  private appliedInitial: string | undefined;
  /** De intro klapt eenmalig vanzelf dicht, bij de eerste duiding. */
  private introAutoCollapsed = false;
  /** Laatst toegepaste context (referentie), zodat dezelfde context niet opnieuw het concept zet. */
  private appliedContext: IChingConsultContext | undefined;
  private lastEmittedSave: PlatformIChingConsultationDto | undefined;

  constructor() {
    // Ingebed blijft het concept in het geheugen: het concept van de pagina wordt niet overschreven.
    effect(() => {
      const draft = this.draft();
      if (!this.embedded()) {
        saveIChingDraft(draft);
      }
    });

    effect(() => {
      const context = this.context();
      if (!this.embedded() || context === undefined || context === this.appliedContext) {
        return;
      }
      this.appliedContext = context;
      untracked(() => this.applyContext(context));
    });

    effect(() => {
      const result = this.session.lastSaved();
      if (result === undefined || result === this.lastEmittedSave) {
        return;
      }
      this.lastEmittedSave = result;
      this.timeEditOverride.set(false);
      this.saved.emit(result);
    });

    effect(() => {
      const lang = this.contentLang.effectiveLang();
      untracked(() => void this.loadMethod(lang));
    });

    effect(() => {
      const key = this.readingContentKey();
      untracked(() => void this.readingContent.load(key));
    });

    // Na het eerste bezoek: zodra er een duiding is, klapt de intro dicht (openen kan nog altijd).
    effect(() => {
      if (this.reading() === undefined || this.introAutoCollapsed) {
        return;
      }
      this.introAutoCollapsed = true;
      untracked(() => {
        if (!this.settings.ichingIntroCollapsed()) {
          this.settings.setIChingIntroCollapsed(true);
        }
      });
    });

    effect(() => {
      const initial = this.initial();
      if (initial === undefined) {
        // Gewist (de pagina haalde de parameters weg): dezelfde deeplink mag later opnieuw.
        this.appliedInitial = undefined;
        return;
      }
      const key = `${initial.hexagram}:${initial.moving.join(',')}`;
      if (key === this.appliedInitial) {
        return;
      }
      this.appliedInitial = key;
      untracked(() => {
        this.settings.setIChingCastMode('direct');
        this.draft.set({ ...EMPTY_ICHING_DRAFT, castMode: 'direct', lines: linesFromHexagram(initial.hexagram, initial.moving) });
        this.session.markDirty();
        this.view.set('consult');
        this.initialApplied.emit();
      });
    });
  }

  ngOnInit(): void {
    if (!this.embedded()) {
      this.draft.set(loadIChingDraft());
    }
  }

  /** Context van de host: een bestaande raadpleging in de leesstand, of een schoon concept met chart en tijdstip. */
  private applyContext(context: IChingConsultContext): void {
    this.timeEditOverride.set(false);
    if (context.consultation !== undefined) {
      this.session.reopen(context.consultation);
      return;
    }
    this.draft.set({
      ...EMPTY_ICHING_DRAFT,
      castMode: this.castMode(),
      linkedChartId: context.chartId,
      consultedAt: context.atUtc ?? moment().toISOString(),
      consultedAtPinned: true,
    });
    this.session.markDirty();
  }

  protected stepById(id: IChingMethodStep['id']): IChingMethodStep | undefined {
    return this.method()?.steps.steps.find(step => step.id === id);
  }

  // ── Contextkaart: tijdstip van de raadpleging ──────────────────────────

  /** Nieuw tijdstip uit het `datetime-local`-veld (lokale tijd); leeg laat het staan. */
  protected setConsultedAt(value: string): void {
    const local = moment(value, 'YYYY-MM-DDTHH:mm', true);
    if (!local.isValid()) {
      return;
    }
    const consultedAt = local.toISOString();
    if (consultedAt === this.draft().consultedAt) {
      return;
    }
    this.draft.update(draft => ({ ...draft, consultedAt, consultedAtPinned: true }));
    this.session.markDirty();
  }

  protected enableTimeEdit(): void {
    this.timeEditOverride.set(true);
  }

  protected retryMethod(): void {
    void this.loadMethod(this.contentLang.effectiveLang());
  }

  protected openGuide(event: MouseEvent): void {
    if (this.method() === undefined) {
      return;
    }
    this.guidePanel.open({ x: event.clientX, y: event.clientY });
  }

  protected async newConsultation(): Promise<void> {
    const confirmed = await this.confirmer.confirm(this.t('humanDesign.iching.consult.newConsultationConfirm'), {
      color: 'warning',
    });
    if (!confirmed) {
      return;
    }
    clearIChingDraft();
    this.draft.set({ ...EMPTY_ICHING_DRAFT, castMode: this.castMode() });
    this.session.markDirty();
    this.timeEditOverride.set(false);
    this.view.set('consult');
  }

  // ── Dagboek ────────────────────────────────────────────────────────────

  protected setView(view: IChingConsultView): void {
    this.view.set(view);
  }

  /** Heropent een raadpleging uit het dagboek in fase 3; onbewaarde invoer pas na bevestiging weg. */
  protected async reopen(dto: PlatformIChingConsultationDto): Promise<void> {
    if (!dto.id) {
      return;
    }
    if (hasUnsavedContent(this.draft())) {
      const confirmed = await this.confirmer.confirm(this.t('humanDesign.iching.consult.reopenConfirm'), { color: 'warning' });
      if (!confirmed) {
        return;
      }
    }
    if (!this.session.reopen(dto)) {
      return;
    }
    this.timeEditOverride.set(false);
    this.view.set('consult');
    afterNextRender(
      () => this.host.nativeElement.querySelector('[data-phase="3"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      { injector: this.injector },
    );
  }

  protected consultationDeleted(id: string): void {
    this.session.forget(id);
  }

  // ── Fase 1 ─────────────────────────────────────────────────────────────

  protected setQuestion(question: string): void {
    this.draft.update(draft => ({ ...draft, question }));
    this.session.markDirty();
  }

  protected setQuestionKind(questionKind: IChingDraft['questionKind']): void {
    this.draft.update(draft => ({ ...draft, questionKind }));
  }

  protected setIntroCollapsed(collapsed: boolean): void {
    this.settings.setIChingIntroCollapsed(collapsed);
  }

  // ── Fase 2 ─────────────────────────────────────────────────────────────

  protected setCastMode(castMode: IChingCastMode): void {
    this.settings.setIChingCastMode(castMode);
    this.draft.update(draft => ({ ...draft, castMode }));
  }

  protected setDistribution(distribution: IChingCastDistribution): void {
    this.settings.setIChingCastDistribution(distribution);
  }

  /** Een andere worp is een andere raadpleging: niet meer bewaard, halve handmatige invoer vervalt. */
  protected setLines(lines: IChingLineValue[]): void {
    const draft = this.draft();
    if (lines.join() === draft.lines.join()) {
      return;
    }
    // Muntworpen horen bij de lijnen eronder: wat boven de nieuwe lengte zit, vervalt.
    // Het tijdstip van de raadpleging is het ogenblik waarop de zesde lijn valt; een nieuwe worp
    // krijgt een nieuw tijdstip. Een gepind tijdstip (moment, transits, zelf gekozen) blijft staan.
    const consultedAt = draft.consultedAtPinned
      ? draft.consultedAt
      : lines.length === 6
        ? (draft.consultedAt ?? moment().toISOString())
        : undefined;
    this.draft.set({
      ...withoutSaved(draft),
      lines,
      coinFaces: draft.coinFaces.slice(0, lines.length),
      manualLines: undefined,
      consultedAt,
    });
    this.session.markDirty();
  }

  protected setManualLines(manualLines: (IChingLineValue | undefined)[]): void {
    this.draft.update(draft => ({ ...draft, manualLines }));
  }

  protected addCoinFaces(faces: [IChingCoinFace, IChingCoinFace, IChingCoinFace]): void {
    this.draft.update(draft => ({ ...draft, coinFaces: [...draft.coinFaces, faces] }));
  }

  // ── Fase 3 ─────────────────────────────────────────────────────────────

  /** Reflectie in het concept; wat in het dagboek komt, maakt "bewaard" ongeldig. */
  protected updateDraft(patch: IChingReflectionPatch): void {
    this.draft.update(draft => ({ ...draft, ...patch }));
    if (!('relationMode' in patch) && !('nameRevealed' in patch)) {
      this.session.markDirty();
    }
  }

  protected openHexagram(number: number): void {
    this.openHexagramPanel(number);
  }

  protected loadCommentary(): void {
    void this.readingContent.loadCommentary();
  }

  protected save(): void {
    void this.session.save();
  }

  protected revisit(): void {
    void this.session.revisit();
  }

  protected async copy(): Promise<void> {
    const reading = this.reading();
    if (reading === undefined) {
      return;
    }
    const draft = this.draft();
    const text = buildConsultationText({
      question: draft.question,
      questionKind: draft.questionKind,
      lines: draft.lines,
      reading,
      hexagramTitle: this.readingContent.hexagram()?.title,
      changedTitle: this.readingContent.changedHexagram()?.title,
      keywords: draft.keywords,
      doList: draft.doList,
      dontList: draft.dontList,
      notes: draft.notes,
      date: moment().format('LLL'),
      labels: {
        question: this.t('humanDesign.iching.practice.text.question'),
        situation: this.t('humanDesign.iching.practice.text.situation'),
        lines: this.t('humanDesign.iching.practice.text.lines'),
        moving: this.t('humanDesign.iching.cast.movingLines'),
        changed: this.t('humanDesign.iching.reading.changeTitle'),
        keywords: this.t('humanDesign.iching.practice.keywords'),
        do: this.t('humanDesign.iching.practice.do'),
        dont: this.t('humanDesign.iching.practice.dont'),
        notes: this.t('humanDesign.iching.practice.notes'),
      },
    });
    try {
      await navigator.clipboard.writeText(text);
      this.notifier.success(this.t('humanDesign.iching.practice.copied'));
    } catch {
      this.notifier.error(this.t('humanDesign.iching.practice.copyError'));
    }
  }

  // ── Content ────────────────────────────────────────────────────────────

  private async loadMethod(lang: string): Promise<void> {
    const generation = ++this.methodGeneration;
    this.methodError.set(false);
    try {
      const method = await this.content.loadMethod(lang);
      if (generation === this.methodGeneration) {
        this.method.set(method);
      }
    } catch {
      // Bij een taalwissel blijft de vorige content staan; zonder content: melding met opnieuw proberen.
      if (generation === this.methodGeneration && this.method() === undefined) {
        this.methodError.set(true);
      }
    }
  }
}

/** Invoer die nog niet in het dagboek staat en bij heropenen verloren zou gaan. */
function hasUnsavedContent(draft: IChingDraft): boolean {
  if (draft.savedId !== undefined) {
    return false;
  }
  return draft.lines.length > 0 || draft.question.trim().length > 0 || draft.notes.trim().length > 0;
}
